// Os links de arquivo grande que esta caixa criou.
//
// POR QUE ESTA TELA PRECISOU EXISTIR. A API de anexo grande nasceu completa —
// subir, listar e revogar — e a interface só usava SUBIR. O resultado era que
// anexar um arquivo de 180 MB criava um endereço PÚBLICO, válido por 30 dias,
// e a pessoa não tinha nenhuma forma de ver o que havia criado nem de matar um
// link mandado para o endereço errado. A metade perigosa do recurso estava no
// ar e a metade do controle não.
//
// É a mesma tela das "Sessões abertas", e de propósito: a pergunta é a mesma
// ("o que está aberto em meu nome?") e a resposta é o mesmo botão.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import { dataCurta, tamanho } from "../ui/formato";
import { Secao } from "./DoisFatores";

/**
 * O estado em palavra e cor, e o `ativo` é o único que NÃO ganha etiqueta.
 *
 * Pintar os quatro estados faria a lista inteira virar etiqueta colorida e
 * nenhuma chamaria atenção. O que importa de relance é o que ainda abre.
 */
const ETIQUETA: Record<string, { texto: string; classe: string }> = {
  vencido: { texto: "venceu", classe: "bg-linha text-tinta-fraca" },
  revogado: { texto: "revogado", classe: "bg-linha text-tinta-fraca" },
  apagado: { texto: "saiu do disco", classe: "bg-linha text-tinta-fraca" },
};

export function Arquivos() {
  const fila = useQueryClient();
  const lista = useQuery({
    queryKey: ["conta", "arquivos"],
    queryFn: () => correio.arquivosGuardados(),
  });
  const revogar = useMutation({
    mutationFn: (uuid: string) => correio.revogarArquivo(uuid),
    onSuccess: () => void fila.invalidateQueries({ queryKey: ["conta", "arquivos"] }),
  });

  const itens = lista.data?.itens ?? [];
  const ativos = itens.filter((a) => a.estado === "ativo").length;

  return (
    <Secao titulo="Arquivos enviados por link" icone="Link2">
      <p className="text-sm text-tinta-media">
        O arquivo que não cabe como anexo vira um endereço de download, e esse
        endereço é <strong>público</strong>: quem tiver o link baixa, sem
        senha. Mandou para a pessoa errada? Revogue — o link morre na hora.
      </p>

      {lista.isError && <p className="mt-3 text-sm text-perigo">{explicar(lista.error)}</p>}

      {/* Lista vazia é resposta, não ausência de resposta. Sem esta linha a
          seção parece quebrada para quem nunca mandou arquivo grande. */}
      {!lista.isLoading && !lista.isError && itens.length === 0 && (
        <p className="mt-4 rounded-lg border border-linha px-4 py-6 text-center text-sm text-tinta-fraca">
          Você ainda não enviou nenhum arquivo por link.
        </p>
      )}

      {itens.length > 0 && (
        <ul className="mt-4 divide-y divide-linha rounded-lg border border-linha">
          {itens.map((a) => {
            const etiqueta = ETIQUETA[a.estado ?? ""];
            return (
              <li key={a.uuid} className="flex items-center gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-tinta">
                    {a.nome}
                    {etiqueta && (
                      <span
                        className={`ml-2 rounded-full px-2 py-0.5 text-xs font-normal ${etiqueta.classe}`}
                      >
                        {etiqueta.texto}
                      </span>
                    )}
                  </span>
                  {/* As baixas são o que a pessoa de fato quer saber: "já
                      chegou?" e "quantas vezes isso foi baixado?". */}
                  <span className="block text-xs text-tinta-fraca">
                    {tamanho(a.bytes)} · enviado {dataCurta(a.criado_em)} ·{" "}
                    {a.estado === "ativo"
                      ? `vence ${dataCurta(a.expira_em)}`
                      : `venceu ${dataCurta(a.expira_em)}`}{" "}
                    · {a.baixas === 1 ? "1 download" : `${a.baixas ?? 0} downloads`}
                  </span>
                </span>
                {/* Só o que ainda abre tem o que revogar. Botão em link morto
                    é botão que não faz nada, e isso ensina a desconfiar do
                    botão quando ele importa. */}
                {a.estado === "ativo" && (
                  <button
                    onClick={() => revogar.mutate(a.uuid!)}
                    disabled={revogar.isPending}
                    className="text-xs text-perigo underline disabled:opacity-50"
                  >
                    Revogar
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {revogar.isError && <p className="mt-3 text-sm text-perigo">{explicar(revogar.error)}</p>}

      {ativos > 0 && (
        <p className="mt-3 text-xs text-tinta-fraca">
          {ativos === 1 ? "1 link ainda abre." : `${ativos} links ainda abrem.`} Links
          vencidos param de funcionar sozinhos e o arquivo sai do disco.
        </p>
      )}
    </Secao>
  );
}
